import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Loading } from 'features/loading';
import { getToken /*, keycloak */ } from 'shared/keycloak';
import {
  ESIA_CODE_STORAGE_KEY,
  FORM_STATE_STORAGE_KEY,
  LAST_PATH_STORAGE_KEY,
  QUESTIONNAIRE_SUMBITTED_ERROR,
  SESSION_ID_STORAGE_KEY,
} from 'shared/config';
import { FormValues, DataFromEsia, Retailer } from '../types';
import { getQuestionnaire, loadDataFromEsia, redirectToEsia, submitQuestionnaire } from '../api';
import { businessUnitByRetailerName } from '../constants';
import { QuestionnaireForm } from './questionnaire-form';
import styles from './page.module.scss';
import { Header } from './header';
import { StatusModal, Gosuslugi } from './';

type GenericErrorType =
  | 'auth-load'
  | 'questionnaire-load'
  | 'esia-data'
  | 'esia-session'
  | 'esia-auth'
  | 'esia-redirect'
  | 'submit';

const ERROR_SUBTITLES: Record<GenericErrorType, string> = {
  'auth-load': 'Не удалось подтвердить авторизацию',
  'questionnaire-load': 'Не удалось загрузить анкету',
  'esia-data': 'Не удалось получить данные из Госуслуг',
  'esia-session': 'Не удалось получить данные сессии',
  'esia-auth': 'Не удалось авторизоваться перед переходом к Госуслугам',
  'esia-redirect': 'Не удалось перейти на Госуслуги',
  'submit': 'Не удалось отправить анкету',
};

type QuestionnairePageProps = {
  retailer: Retailer;
};

export const QuestionnairePage = ({ retailer }: QuestionnairePageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const esiaCode = localStorage.getItem(ESIA_CODE_STORAGE_KEY);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<GenericErrorType | 'forbidden' | null>(null);
  const [isInaccessible, setIsInaccessible] = useState(false);
  const [esiaData, setEsiaData] = useState<DataFromEsia | undefined>(undefined);

  //TODO: нужно? Если да вынести выше
  useEffect(() => {
    localStorage.setItem(LAST_PATH_STORAGE_KEY, location.pathname);
  }, [location.pathname]);

  const businessUnit = businessUnitByRetailerName[retailer];

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = await getToken();
        if (!token) {
          setError('auth-load');
          return;
        }

        const response = await getQuestionnaire(token, businessUnit);

        if ('isError' in response) {
          if (response.detail === QUESTIONNAIRE_SUMBITTED_ERROR) {
            setIsSubmitted(true);
          } else {
            setError('questionnaire-load');
          }

          return;
        }

        if (response.businessUnit !== businessUnit) {
          setIsInaccessible(true);
          return;
        }

        if (!esiaCode) {
          return;
        }

        setIsLoading(true);
        const dataFromEsia = await loadDataFromEsia(token, businessUnit, esiaCode);

        if ('isError' in dataFromEsia) {
          setError(dataFromEsia.status === 403 ? 'forbidden' : 'esia-data');
          return;
        }

        setEsiaData(dataFromEsia);
      } finally {
        setIsLoading(false);
        localStorage.removeItem(ESIA_CODE_STORAGE_KEY);
      }
    };

    fetch();
  }, [businessUnit, esiaCode, navigate]);

  const handleRedirectToEsia = useCallback(() => {
    // ЕСИА (epgu-esia-adapter на стороне НСИ) валидирует параметр state как UUID — это требование
    // Минцифры. Раньше в state переиспользовался keycloak.sessionId (claim session_state из
    // access-токена), и это работало, пока Keycloak отдавал session_state в формате UUID.
    // В свежих версиях Keycloak session_state стал короткой base64-строкой (по OIDC sid — это
    // opaque string, формат не гарантирован), из-за чего epgu-esia-adapter начал отвечать 400
    // "body.state: Input should be a valid UUID". Генерируем свежий UUIDv4 на каждый редирект
    // и кладём его в localStorage, чтобы EsiaCallbackPage смогла сверить state при возврате
    // от Госуслуг (CSRF-защита OAuth flow).
    // const sessionId = keycloak?.sessionId;
    // if (!sessionId) {
    //   setError('esia-session');
    //   return;
    // }
    const sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);

    setIsLoading(true);
    getToken()
      .then((token) => {
        if (!token) {
          throw 'esia-auth';
        }

        return redirectToEsia(token, businessUnit, sessionId);
      })
      .then((response) => {
        if ('isError' in response) {
          setError('esia-redirect');
          return;
        }

        window.location.href = response.link;
        return;
      })
      .catch((e) => {
        setError(e === 'esia-auth' ? 'esia-auth' : 'esia-redirect');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [businessUnit]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      setIsLoading(true);

      getToken()
        .then((token) => {
          if (!token) {
            throw new Error('auth');
          }

          return submitQuestionnaire(token, businessUnit, values);
        })
        .then((response) => {
          if ('isError' in response) {
            setError('submit');
            return;
          }

          setIsSuccess(true);
          localStorage.removeItem(FORM_STATE_STORAGE_KEY);
        })
        .catch(() => {
          setError('submit');
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [businessUnit],
  );

  const requestStatus = useMemo(() => {
    if (isLoading) {
      return;
    } else if (isInaccessible) {
      return 'inaccessible';
    } else if (error) {
      return 'failed';
    } else if (isSuccess) {
      return 'success';
    }
  }, [error, isSuccess, isInaccessible, isLoading]);

  if (isSubmitted) {
    return <StatusModal type="success" />;
  }

  return (
    <div className={styles.page}>
      <Header retailer={retailer} />
      <div className={styles.content}>
        <Gosuslugi onEsiaRedirectAction={handleRedirectToEsia} isLoading={isLoading} />
        <QuestionnaireForm
          dataFromEsia={esiaData}
          retailer={retailer}
          isLoading={isLoading}
          onSubmitAction={handleSubmit}
          onEsiaRedirectAction={handleRedirectToEsia}
        />
        <div className={styles.infoConsent}>Достоверность указанной выше информации подтверждаю</div>
      </div>
      {!!requestStatus && (
        <StatusModal
          type={error === 'forbidden' ? 'forbidden' : requestStatus}
          subtitle={
            isInaccessible
              ? 'Анкета недоступна для этого раздела'
              : error && error !== 'forbidden'
                ? ERROR_SUBTITLES[error]
                : undefined
          }
          onClose={() => setError(null)}
        />
      )}
      {isLoading && <Loading />}
    </div>
  );
};
